// src/components/IsEmriDetay.js

import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, onSnapshot, collection, query, orderBy, runTransaction, serverTimestamp, updateDoc } from 'firebase/firestore';
import AddPaymentModal from './AddPaymentModal';
import { useAuth } from '../context/AuthContext';
import { intervalToDuration, isPast } from 'date-fns';
import { Box, Typography, Paper, Grid, CircularProgress, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider, IconButton, Stack, Alert, Link as MuiLink, Chip } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import AddCardIcon from '@mui/icons-material/AddCard';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import logo from '../assets/logo.png';
import { TeslimFormu } from './TeslimFormu';
import NotFoundState from './NotFoundState';
import { closeLinkedAppointments } from './appointmentSync';

// Bu durumlara geçildiğinde bağlı randevu da 'Tamamlandı' yapılır.
const KAPANIS_DURUMLARI = ['Tamamlandı', 'Teslim Edildi'];

// Araç logosunu getiren fonksiyon
const getLogoPath = (markaModel) => {
  if (!markaModel || typeof markaModel !== 'string' || markaModel.trim().length === 0) {
    return '/logos/default.svg';
  }
  const logoName = markaModel.split(' ')[0].toLowerCase().replace(' ', '-');
  return `/logos/${logoName}.svg`;
};

const IsEmriDetay = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [workOrder, setWorkOrder] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [kalanSure, setKalanSure] = useState('');

  const [customerBalance, setCustomerBalance] = useState(null);

  useEffect(() => {
    if (workOrder && workOrder.teslimTarihi) {
      const updateCountdown = () => {
        const teslimTarihiDate = workOrder.teslimTarihi.toDate();
        const now = new Date();
        if (isPast(teslimTarihiDate)) {
          setKalanSure("Teslim tarihi geçti!");
          return;
        }
        const duration = intervalToDuration({ start: now, end: teslimTarihiDate });
        const parts = [];
        if (duration.days > 0) parts.push(`${duration.days} gün`);
        if (duration.hours > 0) parts.push(`${duration.hours} saat`);
        if (duration.minutes > 0) parts.push(`${duration.minutes} dakika`);
        setKalanSure(parts.join(' '));
      };
      updateCountdown();
      const interval = setInterval(updateCountdown, 1000 * 60);
      return () => clearInterval(interval);
    }
  }, [workOrder]);

  useEffect(() => {
    const docRef = doc(db, "workOrders", id);
    const unsubscribeWO = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const woData = { id: docSnap.id, ...docSnap.data() };
        setWorkOrder(woData);

        if (woData.musteriId) {
          const customerRef = doc(db, "customers", woData.musteriId);
          onSnapshot(customerRef, (custSnap) => {
            if (custSnap.exists()) {
              setCustomerBalance(custSnap.data().toplamBakiye || 0);
            }
          });
        }
      } else {
        console.log("İş emri bulunamadı!");
      }
      setLoading(false);
    });
    return () => unsubscribeWO();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const paymentsRef = collection(db, "workOrders", id, "payments");
    const q = query(paymentsRef, orderBy("tarih", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => { setPayments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))) });
    return () => unsubscribe();
  }, [id]);

  const handleAddPayment = async (paymentData) => {
    const workOrderRef = doc(db, "workOrders", id);
    const customerRef = doc(db, "customers", workOrder.musteriId);

    try {
      await runTransaction(db, async (transaction) => {
        const workOrderDoc = await transaction.get(workOrderRef);
        const customerDoc = await transaction.get(customerRef);

        if (!workOrderDoc.exists()) throw "İş emri bulunamadı!";
        if (!customerDoc.exists()) throw "Müşteri bulunamadı!";

        const currentWorkOrderData = workOrderDoc.data();
        const currentCustomerData = customerDoc.data();

        const newPaymentRef = doc(collection(workOrderRef, "payments"));
        transaction.set(newPaymentRef, {
          ...paymentData,
          tarih: serverTimestamp(),
          musteriId: currentWorkOrderData.musteriId,
          musteriAdSoyad: currentWorkOrderData.musteriAdSoyad,
          aracPlaka: currentWorkOrderData.aracPlaka,
        });

        const newTotalPaid_WO = (currentWorkOrderData.odenenTutar || 0) + paymentData.tutar;
        transaction.update(workOrderRef, {
          odenenTutar: newTotalPaid_WO,
          kalanTutar: currentWorkOrderData.toplamTutar - newTotalPaid_WO
        });

        const newToplamOdeme_Cust = (currentCustomerData.toplamOdeme || 0) + paymentData.tutar;
        const newToplamBakiye_Cust = (currentCustomerData.toplamBakiye || 0) - paymentData.tutar;

        transaction.update(customerRef, {
          toplamOdeme: newToplamOdeme_Cust,
          toplamBakiye: newToplamBakiye_Cust
        });
      });
    } catch (e) {
      // Hata YUTULMAZ: modal açık kalsın ve kullanıcı sahte başarı görmesin.
      console.error("Ödeme eklenirken hata: ", e);
      throw e;
    }
  };

  const handleDeletePayment = async (payment) => {
    if (window.confirm(`${(payment.tutar || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })} tutarındaki ödemeyi silmek istediğinizden emin misiniz?`)) {
      const workOrderRef = doc(db, "workOrders", id);
      const paymentRef = doc(workOrderRef, "payments", payment.id);
      const customerRef = doc(db, "customers", workOrder.musteriId);

      try {
        await runTransaction(db, async (transaction) => {
          const workOrderDoc = await transaction.get(workOrderRef);
          const customerDoc = await transaction.get(customerRef);

          if (!workOrderDoc.exists()) throw "İş emri bulunamadı!";
          if (!customerDoc.exists()) throw "Müşteri bulunamadı!";

          const currentWorkOrderData = workOrderDoc.data();
          const currentCustomerData = customerDoc.data();

          transaction.delete(paymentRef);

          const newTotalPaid_WO = (currentWorkOrderData.odenenTutar || 0) - payment.tutar;
          transaction.update(workOrderRef, {
            odenenTutar: newTotalPaid_WO,
            kalanTutar: currentWorkOrderData.toplamTutar - newTotalPaid_WO
          });

          const newToplamOdeme_Cust = (currentCustomerData.toplamOdeme || 0) - payment.tutar;
          const newToplamBakiye_Cust = (currentCustomerData.toplamBakiye || 0) + payment.tutar;

          transaction.update(customerRef, {
            toplamOdeme: newToplamOdeme_Cust,
            toplamBakiye: newToplamBakiye_Cust
          });
        });
      } catch (e) { console.error("Ödeme silinirken hata: ", e); }
    }
  };

  const handleStatusChange = async (newStatus) => {
    const confirmMessage = newStatus === 'Tamamlandı'
      ? 'İş emrini "Tamamlandı" olarak işaretlemek istediğinizden emin misiniz?'
      : `İş emrini "${newStatus}" olarak işaretlemek istediğinizden emin misiniz?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const workOrderRef = doc(db, "workOrders", id);
      await updateDoc(workOrderRef, {
        durum: newStatus,
        updatedAt: serverTimestamp()
      });

      // İş bitince bağlı randevu da kapanır; aksi halde randevu 'Onaylandı'da
      // kalıp takvimde sonsuza dek "Geciken" hayalet olarak duruyordu.
      if (KAPANIS_DURUMLARI.includes(newStatus)) {
        try {
          await closeLinkedAppointments(id, workOrder?.appointmentId);
        } catch (e) {
          console.error("Bağlı randevu kapatılamadı:", e);
        }
      }

      alert(`İş emri durumu "${newStatus}" olarak güncellendi.`);
    } catch (error) {
      console.error("Durum güncellenirken hata:", error);
      alert("Durum güncellenirken bir hata oluştu.");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Beklemede': 'warning',
      'Devam Ediyor': 'info',
      'Tamamlandı': 'success',
      'Teslime Hazır': 'primary',
      'Teslim Edildi': 'secondary',
      'İptal': 'error'
    };
    return colors[status] || 'default';
  };

  if (loading || !currentUser) { return <CircularProgress sx={{ display: 'block', margin: 'auto' }} />; }
  if (!workOrder) {
    return (
      <NotFoundState
        title="İş emri bulunamadı"
        id={id}
        description="Bu iş emri silinmiş olabilir. Eski bir bildirim veya randevu bağlantısından geldiyseniz bağlantı artık geçersizdir."
        backTo="/is-emirleri"
        backLabel="İş Emirleri"
      />
    );
  }

  const isEmriTarihi = workOrder.isEmriTarihi?.toDate().toLocaleDateString('tr-TR');
  const isUsta = currentUser.role === 'Usta';
  const isGenelMudur = currentUser.role === 'Genel Müdür';
  const isMudur = currentUser.role === 'Müdür';

  // Çift-format (G4/G11): eski kayıtlar kalem fiyatlı tabloyla AYNEN, yeni kayıtlar
  // fiyat sütunsuz (iş listesi + kategori tutarları + toplam) gösterilir.
  const uyg = workOrder.uygulamalar || {};
  const kalemFiyatVar = Boolean(
    (uyg.paketler?.length > 0) ||
    [...(uyg.camFilmi || []), ...(uyg.ppf || []), ...(uyg.seramik || [])].some(p => typeof p.fiyat === 'number' && p.fiyat > 0) ||
    (uyg.gocuk?.aktif && Number(uyg.gocuk.fiyat) > 0) ||
    (uyg.yikama?.aktif && Number(uyg.yikama.fiyat) > 0) ||
    (uyg.diger || []).some(h => Number(h.fiyat) > 0)
  );
  const fiyatSutunu = currentUser.role !== 'Usta' && kalemFiyatVar;
  const kategoriTutarEtiketleri = {
    camFilmi: 'Cam Filmi', ppf: 'PPF Kaplama', ppfFullBody: 'PPF Full Body',
    seramik: 'Seramik Kaplama', yikama: 'Yıkama', diger: 'Diğer', gocuk: 'Göçük Düzeltme'
  };
  const kategoriTutarSatirlari = Object.entries(workOrder.kategoriTutarlari || {}).filter(([, v]) => Number(v) > 0);

  return (
    <Box>
      <style>{`
        @media screen {
            .print-only {
                display: none !important;
            }
        }
        @media print {
            body * {
                visibility: hidden;
            }
            .print-only, .print-only * {
                visibility: visible;
            }
            .print-only {
                position: absolute !important;
                left: 0 !important;
                top: 0 !important;
                width: 100% !important;
                height: auto !important;
                z-index: 99999 !important;
                background-color: white !important;
                display: block !important;
                margin: 0 !important;
                padding: 0 !important;
            }
            @page {
                size: A4;
                margin: 5mm;
            }
        }
      `}</style>

      {/* Yazdırılacak Alan (Sadece Printte Görünür) */}
      <Box className="print-only">
        <TeslimFormu workOrder={workOrder} />
      </Box>


      {/* Ekran İçeriği */}
      <Box className="no-print">
        <AddPaymentModal isOpen={isPaymentModalOpen} onClose={() => setIsPaymentModalOpen(false)} onSave={handleAddPayment} kalanTutar={workOrder.kalanTutar ?? 0} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h4" component="h1">İş Emri Detayı: #{workOrder.aracPlaka}</Typography>
            <Chip label={workOrder.durum || 'Beklemede'} color={getStatusColor(workOrder.durum)} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {isUsta && workOrder.durum !== 'Tamamlandı' && workOrder.durum !== 'Teslime Hazır' && workOrder.durum !== 'Teslim Edildi' && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleStatusChange('Tamamlandı')}
              >
                Tamamlandı Yap
              </Button>
            )}
            {(isGenelMudur || isMudur) && workOrder.durum === 'Tamamlandı' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<LocalShippingIcon />}
                onClick={() => handleStatusChange('Teslime Hazır')}
              >
                Teslime Hazır Yap
              </Button>
            )}
            {(isGenelMudur || isMudur) && (
              <Button variant="outlined" color="info" startIcon={<EditIcon />} onClick={() => navigate(`/is-emri/duzenle/${id}`)}>Düzenle</Button>
            )}
            <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>Yazdır / Teslim Formu</Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={currentUser?.role === 'Usta' ? 12 : 8}>
            <Stack spacing={3}>
              <Paper sx={{ p: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Typography variant="h6" gutterBottom>Müşteri Bilgileri</Typography>
                    <Divider />
                    <Box sx={{ mt: 1 }}>
                      <MuiLink component={RouterLink} to={`/musteri/${workOrder.musteriId}`} sx={{ textDecoration: 'none', color: 'inherit', fontWeight: 'bold' }}>{workOrder.musteriAdSoyad}</MuiLink>
                      <Typography><b>Telefon:</b> {workOrder.musteriTelefon}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="h6" gutterBottom>Araç Bilgileri</Typography>
                    <Divider />
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1.5 }}>
                      <Box sx={{ width: 60, height: 45, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa', borderRadius: 1, p: 0.5, border: '1px solid #e0e0e0', flexShrink: 0 }}>
                        <Box component="img" src={getLogoPath(workOrder.aracMarkaModel)} onError={(e) => { e.currentTarget.src = '/logos/default.svg'; }} alt={workOrder.aracPlaka} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                      </Box>
                      <Box>
                        <MuiLink component={RouterLink} to={`/arac/${workOrder.aracId}`} sx={{ textDecoration: 'none', color: 'inherit' }}>
                          <Typography><b>Plaka:</b> {workOrder.aracPlaka}</Typography>
                        </MuiLink>
                        <Typography><b>Marka/Model:</b> {workOrder.aracMarkaModel}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Uygulanan Hizmetler</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Uygulama</TableCell><TableCell>Parça / Detay</TableCell>{fiyatSutunu && <TableCell align="right">Fiyat</TableCell>}</TableRow></TableHead>
                    <TableBody>
                      {workOrder.uygulamalar?.paketler?.map((p, idx) => (
                        <React.Fragment key={`paket-${idx}`}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Paket</TableCell>
                            <TableCell>{p.urunAdi}</TableCell>
                            {fiyatSutunu && <TableCell align="right">{Number(p.fiyat || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>}
                          </TableRow>
                          {p.icindekiHizmetler && p.icindekiHizmetler.map((sub, sIdx) => (
                            <TableRow key={`paket-${idx}-sub-${sIdx}`} sx={{ backgroundColor: '#fafafa' }}>
                              <TableCell sx={{ pl: 4, color: 'text.secondary' }}> ↳ {sub.tip}</TableCell>
                              <TableCell sx={{ color: 'text.secondary' }}>{sub.hizmetAdi}</TableCell>
                              {fiyatSutunu && <TableCell align="right" sx={{ color: 'text.secondary' }}>{sub.fiyat ? Number(sub.fiyat).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' }) : '-'}</TableCell>}
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                      {workOrder.uygulamalar?.camFilmi?.map(p => (<TableRow key={p.ad}><TableCell sx={{ fontWeight: 'bold' }}>Cam Filmi</TableCell><TableCell>{p.ad}: {p.urunAdi}</TableCell>{fiyatSutunu && <TableCell align="right">{Number(p.fiyat || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>}</TableRow>))}
                      {workOrder.uygulamalar?.ppf?.map(p => (<TableRow key={p.ad}><TableCell sx={{ fontWeight: 'bold' }}>PPF Kaplama</TableCell><TableCell>{p.ad}: {p.urunAdi}</TableCell>{fiyatSutunu && <TableCell align="right">{Number(p.fiyat || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>}</TableRow>))}
                      {workOrder.uygulamalar?.ppfFullBody?.map((p, i) => (<TableRow key={`pfb-${i}`}><TableCell sx={{ fontWeight: 'bold' }}>PPF Full Body</TableCell><TableCell>{p.urunAdi} (Tüm Gövde)</TableCell>{fiyatSutunu && <TableCell align="right">-</TableCell>}</TableRow>))}
                      {workOrder.uygulamalar?.seramik?.map(p => (<TableRow key={p.ad}><TableCell sx={{ fontWeight: 'bold' }}>Seramik</TableCell><TableCell>{p.ad}: {p.urunAdi}</TableCell>{fiyatSutunu && <TableCell align="right">{Number(p.fiyat || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>}</TableRow>))}
                      {workOrder.uygulamalar?.gocuk?.aktif && (<TableRow><TableCell sx={{ fontWeight: 'bold' }}>Göçük Düzeltme</TableCell><TableCell>{workOrder.uygulamalar.gocuk.aciklama || 'Göçük Düzeltme Hizmeti'}</TableCell>{fiyatSutunu && <TableCell align="right">{Number(workOrder.uygulamalar.gocuk.fiyat || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>}</TableRow>)}
                      {workOrder.uygulamalar?.yikama?.aktif && (<TableRow><TableCell sx={{ fontWeight: 'bold' }}>Yıkama</TableCell><TableCell>Genel Yıkama Hizmeti</TableCell>{fiyatSutunu && <TableCell align="right">{Number(workOrder.uygulamalar.yikama.fiyat || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>}</TableRow>)}
                      {workOrder.uygulamalar?.diger?.filter(h => h.aciklama).map((h, i) => (<TableRow key={i}><TableCell sx={{ fontWeight: 'bold' }}>Diğer</TableCell><TableCell>{h.aciklama}</TableCell>{fiyatSutunu && <TableCell align="right">{Number(h.fiyat || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>}</TableRow>))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {currentUser.role !== 'Usta' && kategoriTutarSatirlari.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="subtitle2" gutterBottom>Kategori Tutarları</Typography>
                    {kategoriTutarSatirlari.map(([key, tutar]) => (
                      <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 360 }}>
                        <Typography variant="body2">{kategoriTutarEtiketleri[key] || key}</Typography>
                        <Typography variant="body2" fontWeight="bold">{Number(tutar).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Notlar ve Tarihler</Typography><Divider sx={{ mb: 2 }} />
                {kalanSure && (<Alert severity={kalanSure === "Teslim tarihi geçti!" ? "error" : "info"} icon={<AccessTimeIcon fontSize="inherit" />} sx={{ mb: 2 }} className="no-print"><Typography>Teslime Kalan Süre: <strong>{kalanSure}</strong></Typography></Alert>)}
                <Typography><b>Oluşturulma Tarihi:</b> {workOrder.isEmriTarihi?.toDate().toLocaleString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
                <Typography><b>Planlanan Teslim Tarihi:</b> {workOrder.teslimTarihi?.toDate().toLocaleString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
                {workOrder.teslimEdilmeTarihi && <Typography><b>Teslim Edilme Tarihi:</b> {workOrder.teslimEdilmeTarihi?.toDate().toLocaleString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>}
                <Typography sx={{ mt: 1 }}><b>Notlar:</b> {workOrder.isEmriNotlari || '-'}</Typography>
              </Paper>
            </Stack>
          </Grid>

          {currentUser?.role !== 'Usta' && (
            <Grid item xs={12} md={4} className="no-print">
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>İş Emri Finansalı</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Yalnızca bu iş emri — tutarlar KDV hariçtir.
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography><b>Toplam:</b> {(workOrder.toplamTutar ?? 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</Typography>
                <Typography color="success.main"><b>Ödenen:</b> {(workOrder.odenenTutar ?? 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</Typography>
                <Typography color="error" variant="h6"><b>Bu iş emrinin kalanı:</b> {(workOrder.kalanTutar ?? 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</Typography>

                <Paper variant="outlined" sx={{ mt: 3, mb: 2, p: 1.5, backgroundColor: '#fff3e0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                    <AccountBalanceWalletIcon fontSize="small" />
                    <Typography variant="caption">MÜŞTERİ GENEL BAKİYESİ (tüm iş emirleri + cari hareketler)</Typography>
                  </Box>
                  <Typography variant="h6" fontWeight="bold" color={customerBalance > 0 ? "error.main" : "success.main"} align="right">
                    {customerBalance !== null ? customerBalance.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' }) : 'Yükleniyor...'}
                  </Typography>
                </Paper>

                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}><Typography variant="h6">Ödemeler</Typography><Button variant="outlined" size="small" startIcon={<AddCardIcon />} onClick={() => setIsPaymentModalOpen(true)}>Ödeme Ekle</Button></Box>
                <TableContainer><Table size="small"><TableHead><TableRow><TableCell>Tarih</TableCell><TableCell>Yöntem</TableCell><TableCell align="right">Tutar</TableCell><TableCell></TableCell></TableRow></TableHead><TableBody>{payments.map(p => (<TableRow key={p.id}><TableCell>{p.tarih?.toDate().toLocaleDateString('tr-TR')}</TableCell><TableCell>{p.odemeYontemi}</TableCell><TableCell align="right">{(p.tutar || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell><TableCell><IconButton size="small" color="error" onClick={() => handleDeletePayment(p)}><DeleteForeverIcon fontSize="small" /></IconButton></TableCell></TableRow>))}</TableBody></Table></TableContainer>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box >
  );
};
export default IsEmriDetay;
